import { useState, useRef, useCallback, useEffect } from "react";
import { api } from "../lib/api";
import {
    Upload,
    X,
    Loader2,
    FileJson,
    CheckCircle2,
    AlertCircle,
    GalleryHorizontalEnd,
    Image as ImageIcon,
    ChevronLeft,
    ChevronRight,
    Star,
    Flag,
} from "lucide-react";
import { toast } from "sonner";

const PIECE_TYPES = [
    { id: "social_post", label: "Redes sociales", color: "#ff9ec7" },
    { id: "website_banner", label: "Banner web", color: "#8fe7d0" },
    { id: "ppt_slide", label: "Slide PPT", color: "#ffbe7b" },
    { id: "other", label: "Otro", color: "#a5a6ff" },
];

const MODES = [
    {
        id: "single",
        label: "Imágenes individuales",
        icon: ImageIcon,
        hint: "Sube piezas sueltas con su propia categoría",
    },
    {
        id: "carousel",
        label: "Carrusel",
        icon: GalleryHorizontalEnd,
        hint: "Sube un lote que forma un carrusel. Marca portada y cierre.",
    },
];

/**
 * Upload modal with two modes:
 * - single: each file is an independent reference
 * - carousel: files form one carousel. User marks one as cover, one as closing, reorders.
 */
export default function UploadModal({ category = "own", onClose, onUploaded }) {
    const [mode, setMode] = useState("single");
    // single-mode state
    const [stage, setStage] = useState("pick"); // pick | uploading | done
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef(null);

    // carousel-mode state
    const [carouselTitle, setCarouselTitle] = useState("");
    const [carouselStage, setCarouselStage] = useState("pick"); // pick | uploading | done
    const [carouselItems, setCarouselItems] = useState([]); // {id, file, role, position}
    const [carouselResult, setCarouselResult] = useState(null);
    /** Sequential upload progress for carousels.
     *  current   — index of slide being uploaded (1-based for display)
     *  total     — total slides
     *  completed — array of refs already created
     *  errored   — array of {index, name, error}
     */
    const [carouselProgress, setCarouselProgress] = useState({
        current: 0,
        total: 0,
        completed: [],
        errored: [],
    });

    const addFiles = useCallback((list) => {
        const arr = Array.from(list).filter((f) => f.type.startsWith("image/"));
        if (!arr.length) return;
        setFiles((prev) => [
            ...prev,
            ...arr.map((f) => ({
                id: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`,
                file: f,
                piece_type: "social_post",
                status: "pending",
                ref: null,
                error: null,
            })),
        ]);
    }, []);

    const addCarouselFiles = useCallback((list) => {
        const arr = Array.from(list).filter((f) => f.type.startsWith("image/"));
        if (!arr.length) return;
        setCarouselItems((prev) => {
            const next = [
                ...prev,
                ...arr.map((f) => ({
                    id: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`,
                    file: f,
                })),
            ];
            return assignDefaultRoles(next);
        });
    }, []);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (mode === "single") addFiles(e.dataTransfer.files);
        else addCarouselFiles(e.dataTransfer.files);
    };

    const setPieceType = (id, piece_type) => {
        setFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, piece_type } : f)),
        );
    };
    const removeFile = (id) =>
        setFiles((prev) => prev.filter((f) => f.id !== id));

    const removeCarousel = (id) => {
        setCarouselItems((prev) => assignDefaultRoles(prev.filter((x) => x.id !== id)));
    };

    const moveCarousel = (id, dir) => {
        setCarouselItems((prev) => {
            const idx = prev.findIndex((x) => x.id === id);
            if (idx < 0) return prev;
            const to = idx + dir;
            if (to < 0 || to >= prev.length) return prev;
            const next = prev.slice();
            [next[idx], next[to]] = [next[to], next[idx]];
            return assignDefaultRoles(next);
        });
    };

    const setRole = (id, role) => {
        setCarouselItems((prev) => {
            const next = prev.map((x) => ({ ...x }));
            const target = next.find((x) => x.id === id);
            if (!target) return prev;
            if (role === "cover") {
                next.forEach((x) => {
                    if (x.id !== id && x.role === "cover") x.role = "body";
                });
            }
            if (role === "closing") {
                next.forEach((x) => {
                    if (x.id !== id && x.role === "closing") x.role = "body";
                });
            }
            target.role = role;
            return next;
        });
    };

    const uploadSingle = async () => {
        if (files.length === 0) return;
        setStage("uploading");
        for (const entry of files) {
            if (entry.status !== "pending") continue;
            setFiles((prev) =>
                prev.map((f) => (f.id === entry.id ? { ...f, status: "uploading" } : f)),
            );
            try {
                const fd = new FormData();
                fd.append("file", entry.file);
                fd.append("category", category);
                fd.append("piece_type", entry.piece_type);
                fd.append("title", entry.file.name);
                const r = await api.post("/references/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === entry.id
                            ? { ...f, status: "analyzing", ref: r.data }
                            : f,
                    ),
                );
                pollAnalysisSingle(entry.id, r.data.id);
            } catch (e) {
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === entry.id
                            ? {
                                  ...f,
                                  status: "error",
                                  error: e?.response?.data?.detail || "Error",
                              }
                            : f,
                    ),
                );
            }
        }
        setStage("done");
        onUploaded?.();
    };

    const pollAnalysisSingle = async (localId, refId) => {
        for (let i = 0; i < 12; i++) {
            await new Promise((r) => setTimeout(r, 5000));
            try {
                const r = await api.get(`/references/${refId}`);
                if (r.data?.analysis) {
                    setFiles((prev) =>
                        prev.map((f) =>
                            f.id === localId
                                ? { ...f, status: "done", ref: r.data }
                                : f,
                        ),
                    );
                    onUploaded?.();
                    return;
                }
            } catch {
                /* keep polling */
            }
        }
        setFiles((prev) =>
            prev.map((f) =>
                f.id === localId && f.status === "analyzing"
                    ? { ...f, status: "done" }
                    : f,
            ),
        );
        onUploaded?.();
    };

    const uploadCarousel = async () => {
        if (carouselItems.length === 0) {
            toast.error("Agrega al menos 1 imagen");
            return;
        }
        const covers = carouselItems.filter((x) => x.role === "cover").length;
        if (covers !== 1) {
            toast.error("Marca exactamente 1 portada");
            return;
        }
        const closings = carouselItems.filter((x) => x.role === "closing").length;
        if (closings > 1) {
            toast.error("Solo puede haber 1 cierre");
            return;
        }
        setCarouselStage("uploading");
        const total = carouselItems.length;
        const title =
            carouselTitle.trim() ||
            `Carrusel ${new Date().toLocaleDateString("es-CO")}`;
        // Reset progress and prepare to fan out the slides one at a time.
        // The first slide mints the carousel_group_id; subsequent calls reuse it.
        setCarouselProgress({
            current: 0,
            total,
            completed: [],
            errored: [],
        });
        let groupId = null;
        const completed = [];
        const errored = [];
        for (let i = 0; i < carouselItems.length; i++) {
            const it = carouselItems[i];
            const slideNum = i + 1;
            setCarouselProgress((p) => ({ ...p, current: slideNum }));
            const fd = new FormData();
            fd.append("file", it.file);
            fd.append("category", category);
            fd.append("carousel_role", it.role);
            fd.append("carousel_position", String(slideNum));
            fd.append("carousel_total", String(total));
            if (groupId) fd.append("carousel_group_id", groupId);
            // Send the group title with EVERY slide so each slide's `title`
            // field reflects the actual carousel name. The backend picks the
            // earliest slide's title for grouping anyway, but persisting the
            // same title across all slides makes the references list cleaner.
            fd.append("carousel_group_title", title);
            try {
                const r = await api.post(
                    "/references/upload/carousel-slide",
                    fd,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 90_000, // 90s per slide is generous; the proxy
                                          // limit is per-request not per-batch.
                    },
                );
                if (!groupId) groupId = r.data.carousel_group_id;
                completed.push(r.data.item);
                setCarouselProgress((p) => ({
                    ...p,
                    completed: [...p.completed, r.data.item],
                }));
            } catch (e) {
                const msg = e?.response?.data?.detail || e?.message || "Error";
                errored.push({ index: slideNum, name: it.file.name, error: msg });
                setCarouselProgress((p) => ({
                    ...p,
                    errored: [
                        ...p.errored,
                        { index: slideNum, name: it.file.name, error: msg },
                    ],
                }));
                // Continue uploading the rest — the user can decide whether
                // to keep a partial carousel or retry.
            }
        }

        setCarouselResult({
            carousel_group_id: groupId,
            title,
            items: completed,
        });
        setCarouselStage("done");
        onUploaded?.();
        if (errored.length === 0) {
            toast.success(`Carrusel creado · ${completed.length} slides`);
        } else if (completed.length === 0) {
            toast.error("No se pudo subir ningún slide. Inténtalo de nuevo.");
        } else {
            toast.warning(
                `Subidos ${completed.length} de ${total}. Falló${errored.length === 1 ? "" : "n"} ${errored.length}.`,
            );
        }
    };

    const allSingleDone =
        stage === "done" &&
        files.every((f) => f.status === "done" || f.status === "error");

    const close = () => {
        const dirty =
            (stage === "uploading" || (stage === "done" && !allSingleDone)) ||
            carouselStage === "uploading";
        if (dirty && !window.confirm("Hay archivos en progreso. ¿Cerrar de todos modos?")) {
            return;
        }
        onClose?.();
    };

    // Ctrl+V / Cmd+V paste support — pasted images (e.g. screenshots) are
    // funneled into the same handleFiles logic used by drag-and-drop.
    // Ignored while typing in inputs/textareas (so pasting the carousel title
    // still works) and while an upload is in progress.
    useEffect(() => {
        const handlePaste = (e) => {
            const tag = (e.target?.tagName || "").toUpperCase();
            const isEditable =
                tag === "INPUT" ||
                tag === "TEXTAREA" ||
                e.target?.isContentEditable;
            if (isEditable) return;

            const cd = e.clipboardData;
            if (!cd) return;

            // Collect image blobs from both `files` and `items` — different
            // browsers/sources expose them via different channels.
            const blobs = [];
            if (cd.files && cd.files.length) {
                for (const f of cd.files) {
                    if (f.type.startsWith("image/")) blobs.push(f);
                }
            }
            if (blobs.length === 0 && cd.items && cd.items.length) {
                for (const it of cd.items) {
                    if (it.kind === "file" && it.type.startsWith("image/")) {
                        const b = it.getAsFile();
                        if (b) blobs.push(b);
                    }
                }
            }
            if (blobs.length === 0) return;

            // Screenshots typically arrive as `image.png` — give each a
            // unique, timestamped name so they don't collide in the list.
            const ts = new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, 19);
            const named = blobs.map((b, i) => {
                const ext = (b.type.split("/")[1] || "png").split("+")[0];
                const suffix = blobs.length > 1 ? `-${i + 1}` : "";
                const name = `pasted-${ts}${suffix}.${ext}`;
                try {
                    return new File([b], name, {
                        type: b.type,
                        lastModified: Date.now(),
                    });
                } catch {
                    // Fallback for older browsers that don't allow File ctor.
                    return b;
                }
            });

            if (mode === "single") {
                if (stage !== "pick") return;
                e.preventDefault();
                addFiles(named);
                toast.success(
                    `${named.length} imagen${named.length === 1 ? "" : "es"} pegada${
                        named.length === 1 ? "" : "s"
                    }`,
                );
            } else {
                if (carouselStage !== "pick") return;
                e.preventDefault();
                addCarouselFiles(named);
                toast.success(
                    `${named.length} slide${named.length === 1 ? "" : "s"} pegado${
                        named.length === 1 ? "" : "s"
                    }`,
                );
            }
        };
        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [mode, stage, carouselStage, addFiles, addCarouselFiles]);

    return (
        <div
            data-testid="upload-modal"
            className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
            onClick={(e) => e.target === e.currentTarget && close()}
        >
            <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl bg-ink-900 border hairline border-ink-600 flex flex-col overflow-hidden">
                <div className="px-6 py-4 flex items-center justify-between border-b hairline border-ink-600">
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                            Cargar referencias
                        </p>
                        <h2 className="font-display text-xl text-fog-100 mt-1">
                            {category === "own" ? "Mis referencias" : "Referencias externas"}
                        </h2>
                    </div>
                    <button
                        data-testid="upload-modal-close"
                        onClick={close}
                        className="w-9 h-9 rounded-lg border hairline border-ink-600 text-fog-400 hover:text-fog-100 hover:border-fog-100 flex items-center justify-center"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Mode toggle */}
                <div className="px-6 pt-4 flex gap-2 shrink-0">
                    {MODES.map((m) => {
                        const Icon = m.icon;
                        const active = mode === m.id;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                data-testid={`upload-modal-mode-${m.id}`}
                                onClick={() => setMode(m.id)}
                                className={`flex-1 rounded-xl border hairline px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                                    active
                                        ? "border-periwinkle-500 bg-periwinkle-500/10"
                                        : "border-ink-600 hover:border-ink-500 bg-ink-800/40"
                                }`}
                            >
                                <Icon
                                    className={`w-5 h-5 shrink-0 ${
                                        active ? "text-periwinkle-500" : "text-fog-400"
                                    }`}
                                />
                                <div className="min-w-0">
                                    <p
                                        className={`text-sm font-medium ${
                                            active ? "text-periwinkle-400" : "text-fog-100"
                                        }`}
                                    >
                                        {m.label}
                                    </p>
                                    <p className="text-[11px] text-fog-400 truncate">
                                        {m.hint}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {mode === "single" && (
                        <SingleMode
                            stage={stage}
                            files={files}
                            dragOver={dragOver}
                            setDragOver={setDragOver}
                            onDrop={onDrop}
                            onPick={() => fileInput.current?.click()}
                            onChange={(e) => addFiles(e.target.files)}
                            fileInput={fileInput}
                            onSetType={setPieceType}
                            onRemove={removeFile}
                        />
                    )}
                    {mode === "carousel" && (
                        <CarouselMode
                            title={carouselTitle}
                            setTitle={setCarouselTitle}
                            items={carouselItems}
                            stage={carouselStage}
                            result={carouselResult}
                            progress={carouselProgress}
                            dragOver={dragOver}
                            setDragOver={setDragOver}
                            onDrop={onDrop}
                            onPickFiles={(ev) => addCarouselFiles(ev.target.files)}
                            onRemove={removeCarousel}
                            onMove={moveCarousel}
                            onSetRole={setRole}
                        />
                    )}
                </div>

                <div className="px-6 py-4 border-t hairline border-ink-600 flex items-center justify-between gap-3 shrink-0">
                    {mode === "single" ? (
                        <>
                            <p className="font-mono text-[10px] text-fog-500 uppercase tracking-widest">
                                {files.length} archivo{files.length === 1 ? "" : "s"}
                            </p>
                            <div className="flex gap-2">
                                {stage === "pick" && (
                                    <>
                                        <button
                                            onClick={close}
                                            className="rounded-xl border hairline border-ink-600 text-fog-400 hover:text-fog-100 px-5 py-2.5 text-sm"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            data-testid="upload-modal-submit"
                                            onClick={uploadSingle}
                                            disabled={files.length === 0}
                                            className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <Upload className="w-4 h-4" />
                                            Subir {files.length > 0 && `(${files.length})`}
                                        </button>
                                    </>
                                )}
                                {stage !== "pick" && (
                                    <button
                                        data-testid="upload-modal-done"
                                        onClick={() => {
                                            if (!allSingleDone) return;
                                            onClose?.();
                                            toast.success("Referencias agregadas");
                                        }}
                                        disabled={!allSingleDone}
                                        aria-disabled={!allSingleDone}
                                        className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                        {allSingleDone ? "Listo" : "Procesando…"}
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="font-mono text-[10px] text-fog-500 uppercase tracking-widest">
                                {carouselItems.length} slide
                                {carouselItems.length === 1 ? "" : "s"}
                            </p>
                            <div className="flex gap-2">
                                {carouselStage === "pick" && (
                                    <>
                                        <button
                                            onClick={close}
                                            className="rounded-xl border hairline border-ink-600 text-fog-400 hover:text-fog-100 px-5 py-2.5 text-sm"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            data-testid="upload-modal-carousel-submit"
                                            onClick={uploadCarousel}
                                            disabled={carouselItems.length === 0}
                                            className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <GalleryHorizontalEnd className="w-4 h-4" />
                                            Crear carrusel ({carouselItems.length})
                                        </button>
                                    </>
                                )}
                                {carouselStage !== "pick" && (
                                    <button
                                        data-testid="upload-modal-carousel-done"
                                        onClick={() => onClose?.()}
                                        disabled={carouselStage !== "done"}
                                        className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {carouselStage === "done" ? (
                                            <>
                                                <CheckCircle2 className="w-4 h-4" />
                                                Listo
                                            </>
                                        ) : (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Subiendo {carouselProgress.current || 0}/
                                                {carouselProgress.total || carouselItems.length}…
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function assignDefaultRoles(items) {
    // Ensure exactly 1 cover, at most 1 closing.
    if (items.length === 0) return items;
    const next = items.map((x, i) => ({ ...x }));
    const hasCover = next.some((x) => x.role === "cover");
    const hasClosing = next.some((x) => x.role === "closing");
    if (!hasCover) next[0].role = "cover";
    if (!hasClosing && next.length > 1) {
        next[next.length - 1].role = "closing";
    }
    next.forEach((x) => {
        if (!x.role) x.role = "body";
    });
    return next;
}

function SingleMode({
    stage,
    files,
    dragOver,
    setDragOver,
    onDrop,
    onPick,
    onChange,
    fileInput,
    onSetType,
    onRemove,
}) {
    return (
        <>
            {stage === "pick" && (
                <div
                    data-testid="upload-modal-dropzone"
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={onPick}
                    className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
                        dragOver
                            ? "border-periwinkle-500 bg-periwinkle-500/5"
                            : "border-ink-600 hover:border-periwinkle-500/60 hover:bg-ink-800/40"
                    }`}
                >
                    <div className="w-14 h-14 rounded-2xl bg-periwinkle-500/10 border hairline border-periwinkle-500/40 mx-auto mb-4 flex items-center justify-center">
                        <Upload className="w-6 h-6 text-periwinkle-500" />
                    </div>
                    <p className="text-fog-100 text-base font-medium">
                        Arrastra tus imágenes aquí
                    </p>
                    <p className="text-fog-400 text-xs mt-1">
                        o haz click para buscar — JPG, PNG, WEBP
                    </p>
                    <p
                        data-testid="upload-modal-paste-hint"
                        className="font-mono text-[10px] tracking-[0.25em] text-fog-500 uppercase mt-3 inline-flex items-center gap-1.5"
                    >
                        <kbd className="rounded border hairline border-ink-600 bg-ink-800/60 px-1.5 py-0.5 text-[10px] text-fog-300">
                            Ctrl
                        </kbd>
                        +
                        <kbd className="rounded border hairline border-ink-600 bg-ink-800/60 px-1.5 py-0.5 text-[10px] text-fog-300">
                            V
                        </kbd>
                        para pegar del portapapeles
                    </p>
                    <input
                        ref={fileInput}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onChange}
                    />
                </div>
            )}
            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((entry) => (
                        <FileRowSingle
                            key={entry.id}
                            entry={entry}
                            editable={stage === "pick"}
                            onSetType={(p) => onSetType(entry.id, p)}
                            onRemove={() => onRemove(entry.id)}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

function CarouselMode({
    title,
    setTitle,
    items,
    stage,
    result,
    progress,
    dragOver,
    setDragOver,
    onDrop,
    onPickFiles,
    onRemove,
    onMove,
    onSetRole,
}) {
    const inputRef = useRef(null);
    const cover = items.find((x) => x.role === "cover");
    const closing = items.find((x) => x.role === "closing");

    return (
        <>
            <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase block mb-2">
                    Título del carrusel
                </label>
                <input
                    data-testid="upload-modal-carousel-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ej: Lanzamiento colección primavera"
                    disabled={stage !== "pick"}
                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm disabled:opacity-60"
                />
            </div>

            {stage === "pick" && (
                <div
                    data-testid="upload-modal-carousel-dropzone"
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                        dragOver
                            ? "border-periwinkle-500 bg-periwinkle-500/5"
                            : "border-ink-600 hover:border-periwinkle-500/60 hover:bg-ink-800/40"
                    }`}
                >
                    <GalleryHorizontalEnd className="w-8 h-8 text-periwinkle-500 mx-auto mb-3" />
                    <p className="text-fog-100 text-sm font-medium">
                        Añade imágenes al carrusel
                    </p>
                    <p className="text-fog-400 text-xs mt-1">
                        Puedes arrastrar varias — luego asigna portada y cierre
                    </p>
                    <p
                        data-testid="upload-modal-carousel-paste-hint"
                        className="font-mono text-[10px] tracking-[0.25em] text-fog-500 uppercase mt-3 inline-flex items-center gap-1.5"
                    >
                        <kbd className="rounded border hairline border-ink-600 bg-ink-800/60 px-1.5 py-0.5 text-[10px] text-fog-300">
                            Ctrl
                        </kbd>
                        +
                        <kbd className="rounded border hairline border-ink-600 bg-ink-800/60 px-1.5 py-0.5 text-[10px] text-fog-300">
                            V
                        </kbd>
                        para pegar del portapapeles
                    </p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onPickFiles}
                    />
                </div>
            )}

            {/* Summary */}
            {items.length > 0 && stage === "pick" && (
                <div className="flex items-center gap-3 flex-wrap text-xs">
                    <span className="font-mono text-[10px] text-fog-500 uppercase tracking-widest">
                        Estructura:
                    </span>
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase ${
                            cover
                                ? "border-periwinkle-500/60 text-periwinkle-400 bg-periwinkle-500/5"
                                : "border-destructive/50 text-destructive"
                        }`}
                    >
                        <Star className="w-3 h-3" /> Portada · {cover ? "✓" : "falta"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border hairline border-ink-600 px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase text-fog-400">
                        Body · {items.filter((x) => x.role === "body").length}
                    </span>
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full border hairline px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase ${
                            closing
                                ? "border-amber-400/60 text-amber-300 bg-amber-400/5"
                                : "border-ink-600 text-fog-500"
                        }`}
                    >
                        <Flag className="w-3 h-3" /> Cierre · {closing ? "✓" : "opcional"}
                    </span>
                </div>
            )}

            {stage === "uploading" && progress && progress.total > 0 && (
                <CarouselProgressBar progress={progress} items={items} />
            )}

            {items.length > 0 && (
                <div className="space-y-2">
                    {items.map((it, i) => (
                        <CarouselRow
                            key={it.id}
                            item={it}
                            index={i}
                            total={items.length}
                            editable={stage === "pick"}
                            onMoveUp={() => onMove(it.id, -1)}
                            onMoveDown={() => onMove(it.id, +1)}
                            onRemove={() => onRemove(it.id)}
                            onSetRole={(r) => onSetRole(it.id, r)}
                        />
                    ))}
                </div>
            )}

            {stage === "done" && result && (
                <div
                    data-testid="upload-modal-carousel-result"
                    className="rounded-2xl border hairline border-emerald-400/40 bg-emerald-400/5 p-4"
                >
                    <p className="font-mono text-[10px] tracking-widest text-emerald-300 uppercase mb-1 flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3" /> Carrusel creado
                    </p>
                    <p className="text-fog-100 text-sm">
                        {result.title} · {result.items?.length} slide
                        {result.items?.length === 1 ? "" : "s"}
                    </p>
                    <p className="text-fog-400 text-xs mt-1">
                        El análisis JSON de cada slide se genera en segundo plano.
                    </p>
                </div>
            )}
        </>
    );
}

function CarouselProgressBar({ progress, items }) {
    const { current, total, completed = [], errored = [] } = progress;
    const done = completed.length;
    const failed = errored.length;
    const processed = done + failed;
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
    const isFinishing = processed >= total;
    const erroredByIndex = new Map(errored.map((e) => [e.index, e]));
    return (
        <div
            data-testid="upload-modal-carousel-progress"
            className="rounded-2xl border hairline border-periwinkle-500/40 bg-periwinkle-500/5 p-4 space-y-3"
        >
            <div className="flex items-center justify-between">
                <div className="min-w-0">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-periwinkle-400 uppercase mb-1 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {isFinishing
                            ? "Finalizando carrusel"
                            : `Subiendo slide ${current} de ${total}`}
                    </p>
                    <p className="text-fog-100 text-sm truncate">
                        {done} listo{done === 1 ? "" : "s"}
                        {failed > 0 ? ` · ${failed} con error` : ""} · {pct}%
                    </p>
                </div>
                <span
                    data-testid="upload-modal-carousel-progress-pct"
                    className="font-mono text-xs text-periwinkle-300 tabular-nums shrink-0 ml-3"
                >
                    {processed} / {total}
                </span>
            </div>

            <div
                className="h-2 rounded-full bg-ink-800 overflow-hidden border hairline border-ink-600"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
            >
                <div
                    data-testid="upload-modal-carousel-progress-bar"
                    className="h-full bg-periwinkle-500 transition-all duration-300 ease-out"
                    style={{ width: `${pct}%` }}
                />
            </div>

            <div className="flex flex-wrap gap-1.5">
                {items.map((it, idx) => {
                    const slideNum = idx + 1;
                    const isCurrent = slideNum === current && !isFinishing;
                    const isDone = slideNum <= current && !erroredByIndex.has(slideNum);
                    const failedHere = erroredByIndex.get(slideNum);
                    let cls =
                        "border-ink-600 bg-ink-800/60 text-fog-500"; // pending
                    let icon = null;
                    let label = `${slideNum}`;
                    if (failedHere) {
                        cls = "border-destructive/60 bg-destructive/10 text-destructive";
                        icon = <AlertCircle className="w-3 h-3" />;
                    } else if (isCurrent) {
                        cls =
                            "border-periwinkle-500 bg-periwinkle-500/10 text-periwinkle-300";
                        icon = <Loader2 className="w-3 h-3 animate-spin" />;
                    } else if (isDone) {
                        cls = "border-emerald-400/60 bg-emerald-400/10 text-emerald-300";
                        icon = <CheckCircle2 className="w-3 h-3" />;
                    }
                    return (
                        <span
                            key={it.id}
                            data-testid={`upload-modal-carousel-progress-slide-${slideNum}`}
                            title={
                                failedHere
                                    ? `Slide ${slideNum} · ${failedHere.error}`
                                    : `Slide ${slideNum}`
                            }
                            className={`inline-flex items-center gap-1 rounded-md border hairline px-2 py-1 font-mono text-[10px] tracking-widest uppercase ${cls}`}
                        >
                            {icon}
                            {label}
                        </span>
                    );
                })}
            </div>

            {failed > 0 && (
                <ul
                    data-testid="upload-modal-carousel-progress-errors"
                    className="text-[11px] text-destructive space-y-0.5 mt-1"
                >
                    {errored.map((e) => (
                        <li key={e.index} className="flex items-start gap-1.5">
                            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                            <span className="truncate">
                                Slide {e.index} · {e.name}: {e.error}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CarouselRow({ item, index, total, editable, onMoveUp, onMoveDown, onRemove, onSetRole }) {
    const preview = item.file ? URL.createObjectURL(item.file) : null;
    const roleMeta = {
        cover: { label: "PORTADA", color: "#a5a6ff", icon: Star },
        body: { label: "BODY", color: "#8fe7d0", icon: ImageIcon },
        closing: { label: "CIERRE", color: "#ffbe7b", icon: Flag },
    }[item.role];
    const Icon = roleMeta?.icon || ImageIcon;
    return (
        <div
            data-testid={`upload-modal-carousel-row-${item.id}`}
            className="rounded-xl border hairline border-ink-600 bg-ink-800/60 p-3 flex items-center gap-3"
        >
            <div className="flex flex-col gap-0.5 shrink-0">
                <button
                    type="button"
                    onClick={onMoveUp}
                    disabled={!editable || index === 0}
                    className="w-6 h-6 rounded border hairline border-ink-600 text-fog-400 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed rotate-90"
                    aria-label="Subir"
                >
                    <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                    type="button"
                    onClick={onMoveDown}
                    disabled={!editable || index === total - 1}
                    className="w-6 h-6 rounded border hairline border-ink-600 text-fog-400 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed rotate-90"
                    aria-label="Bajar"
                >
                    <ChevronRight className="w-3 h-3" />
                </button>
            </div>
            <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden border hairline border-ink-600">
                {preview && (
                    <img
                        src={preview}
                        alt={item.file.name}
                        className="w-full h-full object-cover"
                        onLoad={() => URL.revokeObjectURL(preview)}
                    />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-fog-100 text-sm truncate">{item.file.name}</p>
                <p className="font-mono text-[10px] text-fog-500 mt-0.5">
                    slide {index + 1} / {total} · {(item.file.size / 1024).toFixed(0)} KB
                </p>
            </div>
            <div className="flex gap-1 shrink-0">
                {["cover", "body", "closing"].map((r) => {
                    const active = item.role === r;
                    const meta = {
                        cover: { label: "Portada", color: "#a5a6ff" },
                        body: { label: "Body", color: "#8fe7d0" },
                        closing: { label: "Cierre", color: "#ffbe7b" },
                    }[r];
                    return (
                        <button
                            key={r}
                            type="button"
                            data-testid={`upload-modal-carousel-role-${item.id}-${r}`}
                            onClick={() => editable && onSetRole(r)}
                            disabled={!editable}
                            className={`rounded-md border hairline px-2 py-1 font-mono text-[9px] tracking-widest uppercase transition-colors ${
                                active
                                    ? "bg-ink-700 border-[var(--c)] text-[var(--c)]"
                                    : "border-ink-600 text-fog-400 hover:text-fog-100"
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                            style={{ "--c": meta.color }}
                        >
                            {meta.label}
                        </button>
                    );
                })}
            </div>
            {editable && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="w-8 h-8 rounded-lg text-fog-500 hover:text-destructive flex items-center justify-center shrink-0"
                    aria-label="Quitar"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
            {!editable && (
                <span
                    className="inline-flex items-center gap-1.5 rounded-full border hairline px-2 py-1 font-mono text-[9px] tracking-widest uppercase shrink-0"
                    style={{ borderColor: `${roleMeta.color}66`, color: roleMeta.color }}
                >
                    <Icon className="w-3 h-3" /> {roleMeta.label}
                </span>
            )}
        </div>
    );
}

function FileRowSingle({ entry, editable, onSetType, onRemove }) {
    const [showJson, setShowJson] = useState(false);
    const meta = PIECE_TYPES.find((p) => p.id === entry.piece_type) || PIECE_TYPES[3];
    const previewUrl = entry.file ? URL.createObjectURL(entry.file) : null;
    return (
        <div
            data-testid={`upload-modal-row-${entry.id}`}
            className="rounded-xl border hairline border-ink-600 bg-ink-800/60 p-3"
        >
            <div className="flex items-center gap-3">
                <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden border hairline border-ink-600">
                    {previewUrl && (
                        <img
                            src={previewUrl}
                            alt={entry.file.name}
                            className="w-full h-full object-cover"
                            onLoad={() => URL.revokeObjectURL(previewUrl)}
                        />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-fog-100 text-sm truncate">{entry.file.name}</p>
                    <p className="font-mono text-[10px] text-fog-400 mt-0.5">
                        {(entry.file.size / 1024).toFixed(0)} KB
                    </p>
                </div>
                {editable ? (
                    <select
                        data-testid={`upload-modal-type-${entry.id}`}
                        value={entry.piece_type}
                        onChange={(e) => onSetType(e.target.value)}
                        className="rounded-lg bg-ink-900 border hairline border-ink-600 text-fog-100 text-xs px-3 py-2 outline-none focus:border-periwinkle-500"
                    >
                        {PIECE_TYPES.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span
                        className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border hairline flex items-center gap-1.5"
                        style={{ borderColor: `${meta.color}66`, color: meta.color }}
                    >
                        <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: meta.color }}
                        />
                        {meta.label}
                    </span>
                )}
                <StatusPill status={entry.status} />
                {editable && (
                    <button
                        data-testid={`upload-modal-remove-${entry.id}`}
                        onClick={onRemove}
                        className="w-8 h-8 rounded-lg text-fog-500 hover:text-destructive flex items-center justify-center"
                        aria-label="Quitar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
                {entry.ref?.analysis && !editable && (
                    <button
                        data-testid={`upload-modal-json-${entry.id}`}
                        onClick={() => setShowJson((v) => !v)}
                        className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5"
                    >
                        <FileJson className="w-3 h-3" />
                        {showJson ? "Ocultar" : "JSON"}
                    </button>
                )}
            </div>
            {entry.error && (
                <p className="mt-2 text-xs text-destructive flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" /> {entry.error}
                </p>
            )}
            {showJson && entry.ref?.analysis && (
                <div className="mt-3 rounded-lg bg-ink-950 border hairline border-ink-600 p-3 max-h-60 overflow-y-auto">
                    <pre
                        data-testid={`upload-modal-json-content-${entry.id}`}
                        className="font-mono text-[10px] text-fog-300 whitespace-pre-wrap leading-relaxed"
                    >
                        {JSON.stringify(entry.ref.analysis, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "PENDIENTE", className: "border-ink-600 text-fog-500" },
        uploading: {
            label: "SUBIENDO",
            className: "border-periwinkle-500/50 text-periwinkle-400",
            icon: <Loader2 className="w-3 h-3 animate-spin" />,
        },
        analyzing: {
            label: "ANALIZANDO",
            className: "border-amber-400/50 text-amber-300",
            icon: <Loader2 className="w-3 h-3 animate-spin" />,
        },
        done: {
            label: "LISTO",
            className: "border-emerald-400/50 text-emerald-300",
            icon: <CheckCircle2 className="w-3 h-3" />,
        },
        error: {
            label: "ERROR",
            className: "border-destructive/50 text-destructive",
            icon: <AlertCircle className="w-3 h-3" />,
        },
    };
    const m = map[status] || map.pending;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border hairline px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase shrink-0 ${m.className}`}
        >
            {m.icon}
            {m.label}
        </span>
    );
}
